/**
 * Login page component with Studio Bereikbaar branding
 * Authenticates via the SB Keycloak OIDC broker. Keycloak's login screen
 * federates to Microsoft Entra ID, so the button below still reads
 * "Sign in with Microsoft" — the backend just no longer talks to Microsoft
 * directly (see backend/app/auth/oidc.py).
 */
import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { Logo, BrandedButton } from '@/components/brand'

const Login: React.FC = () => {
  const { user, isLoading, initiateLogin } = useAuth()

  // Show loading while checking authentication
  // This prevents the login page from flashing before redirect
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sb-benzol-green-lightest">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-sb-benzol-green border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <div className="text-sb-benzol-green text-xl font-semibold">
            Loading Studio Bereikbaar Platform...
          </div>
        </div>
      </div>
    )
  }

  // Redirect if already logged in (after loading is complete)
  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  const handleMicrosoftLogin = () => {
    initiateLogin()
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="w-full max-w-lg">
        {/* Logo Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <Logo variant="black" size="lg" />
          </div>
          <h1 className="text-2xl font-semibold text-sb-black mb-3">
            Platform V4
          </h1>
          <p className="text-base text-sb-dark-grey max-w-md mx-auto">
            Access professional mobility data analysis tools with your Studio Bereikbaar Microsoft account.
          </p>
        </div>

        {/* Login Section */}
        <div className="bg-white border border-sb-light-grey rounded-lg p-8 shadow-sm">
          <BrandedButton
            onClick={handleMicrosoftLogin}
            variant="secondary"
            size="lg"
            className="w-full"
          >
            {/* Microsoft Logo */}
            <svg width="20" height="20" viewBox="0 0 23 23">
              <path fill="#f35325" d="M1 1h10v10H1z"/>
              <path fill="#81bc06" d="M12 1h10v10H12z"/>
              <path fill="#05a6f0" d="M1 12h10v10H1z"/>
              <path fill="#ffba08" d="M12 12h10v10H12z"/>
            </svg>
            <span>Sign in with Microsoft</span>
          </BrandedButton>

          {/* Simple Security Note */}
          <p className="text-sm text-sb-dark-grey text-center mt-6">
            Secure authentication via Studio Bereikbaar SSO
          </p>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-sb-dark-grey mt-8">
          <p>© 2024 Studio Bereikbaar</p>
        </div>
      </div>
    </div>
  )
}

export default Login